import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import './Management.css';

const getDescriptionText = (desc) => {
  if (!desc) return '';
  if (typeof desc === 'string') return desc;
  if (Array.isArray(desc))
    return desc.map(b => b.children?.map(c => c.text || '').join('') || '').join(' ');
  return '';
};

const getImageUrl = (image) => {
  if (!image) return null;
  if (Array.isArray(image.data)) return image.data[0]?.attributes?.url || null;
  return image.data?.attributes?.url || null;
};

const ProductManagement = () => {
  const { token } = useAuth();
  const fileRef = useRef();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({ name: '', description: '', price: '', customizable: false });
  const [imageFile, setImageFile] = useState(null);       // new File to upload
  const [imagePreview, setImagePreview] = useState(null); // preview URL
  const [existingImageId, setExistingImageId] = useState(null); // Strapi media ID

  useEffect(() => { fetchProducts(); }, [token]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/products?populate=*&sort=id:desc`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Producten laden mislukt');
      const data = await res.json();
      setProducts(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', price: '' });
    setImageFile(null);
    setImagePreview(null);
    setExistingImageId(null);
    setEditingId(null);
    setShowForm(false);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleEdit = (product) => {
    const attrs = product.attributes || {};
    setFormData({
      name: attrs.name || '',
      description: getDescriptionText(attrs.description),
      price: attrs.price ?? '',
      customizable: !!attrs.customizable,
    });
    const imgUrl = getImageUrl(attrs.image);
    setImagePreview(imgUrl ? `${API_URL}${imgUrl}` : null);
    // Store existing image ID so we can keep it if no new file is chosen
    const imgId = Array.isArray(attrs.image?.data)
      ? attrs.image.data[0]?.id
      : attrs.image?.data?.id ?? null;
    setExistingImageId(imgId);
    setImageFile(null);
    setEditingId(product.id);
    setShowForm(true);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      let imageId = existingImageId;

      // Upload new image if one was selected
      if (imageFile) {
        const fd = new FormData();
        fd.append('files', imageFile);
        const uploadRes = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!uploadRes.ok) throw new Error('Afbeelding uploaden mislukt');
        const uploaded = await uploadRes.json();
        imageId = uploaded[0]?.id ?? null;
      }

      const descriptionBlocks = formData.description
        ? [{ type: 'paragraph', children: [{ type: 'text', text: formData.description }] }]
        : [];

      const body = {
        data: {
          name: formData.name,
          description: descriptionBlocks,
          price: parseFloat(formData.price),
          customizable: formData.customizable,
          ...(imageId !== null ? { image: imageId } : {}),
        },
      };

      const method = editingId ? 'PUT' : 'POST';
      const url = editingId ? `${API_URL}/api/products/${editingId}` : `${API_URL}/api/products`;
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || 'Opslaan mislukt');
      }

      await fetchProducts();
      resetForm();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Product verwijderen?')) return;
    try {
      const res = await fetch(`${API_URL}/api/products/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Verwijderen mislukt');
      await fetchProducts();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Productbeheer</h1>
        <button
          className="btn-primary"
          onClick={() => { if (showForm && !editingId) { resetForm(); } else { resetForm(); setShowForm(true); } }}
        >
          {showForm ? 'Annuleren' : '+ Product Toevoegen'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <h3 style={{ marginBottom: '1rem' }}>{editingId ? 'Product bewerken' : 'Nieuw product'}</h3>

          <div className="form-group">
            <label>Productnaam *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
              required
              placeholder="Bijv. Vinyl sticker set"
            />
          </div>

          <div className="form-group">
            <label>Beschrijving</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(f => ({ ...f, description: e.target.value }))}
              rows="4"
              placeholder="Korte omschrijving van het product..."
            />
          </div>

          <div className="form-group">
            <label>Prijs (€) *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.price}
              onChange={e => setFormData(f => ({ ...f, price: e.target.value }))}
              required
              placeholder="0.00"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.customizable}
                onChange={e => setFormData(f => ({ ...f, customizable: e.target.checked }))}
              />
              Personaliseerbaar (custom sticker met die-cut configurator)
            </label>
          </div>

          <div className="form-group">
            <label>Afbeelding</label>
            {imagePreview && (
              <div className="image-preview-wrap">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <button
                  type="button"
                  className="btn-remove-image"
                  onClick={() => { setImageFile(null); setImagePreview(null); setExistingImageId(null); if (fileRef.current) fileRef.current.value = ''; }}
                >
                  ✕ Verwijderen
                </button>
              </div>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="file-input"
            />
            <span className="field-hint">JPG, PNG, WebP — max 10 MB</span>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={saving}>
              {saving ? 'Opslaan...' : editingId ? 'Bijwerken' : 'Aanmaken'}
            </button>
            <button type="button" className="btn-cancel-form" onClick={resetForm}>
              Annuleren
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="loading">Producten laden...</p>
      ) : products.length === 0 ? (
        <p className="empty">Geen producten gevonden. Voeg er een toe!</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Afbeelding</th>
                <th>Naam</th>
                <th>Beschrijving</th>
                <th>Prijs</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => {
                const attrs = product.attributes || {};
                const imgUrl = getImageUrl(attrs.image);
                return (
                  <tr key={product.id}>
                    <td>
                      {imgUrl
                        ? <img src={`${API_URL}${imgUrl}`} alt={attrs.name} className="table-thumb" />
                        : <span className="no-image">—</span>
                      }
                    </td>
                    <td>{attrs.name}</td>
                    <td className="desc-cell">{getDescriptionText(attrs.description) || '—'}</td>
                    <td>€{parseFloat(attrs.price || 0).toFixed(2)}</td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(product)}>Bewerken</button>
                      <button className="btn-delete" onClick={() => handleDelete(product.id)}>Verwijderen</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProductManagement;
